import axios from 'axios';

// Use backend API URL
// Set REACT_APP_API_BASE_URL to override; set to 'SAME_ORIGIN' to force relative URLs explicitly
const ENV_BASE = process.env.REACT_APP_API_BASE_URL;
export const API_BASE_URL = ENV_BASE === 'SAME_ORIGIN' ? '' : (ENV_BASE || 'https://api.sonickartnow.com');

export const DEFAULT_ADMIN_API_KEY =
  process.env.REACT_APP_ADMIN_API_KEY || 'dev-admin-key-sonic-cart-2024-secure';

export const getAdminAuthHeaders = () => {
  const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
  const adminApiKey =
    localStorage.getItem('adminApiKey') ||
    localStorage.getItem('ADMIN_API_KEY') ||
    DEFAULT_ADMIN_API_KEY;

  const headers = {};
  if (adminToken) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }
  if (adminApiKey) {
    headers['x-admin-key'] = adminApiKey;
  }
  return headers;
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Request interceptor to automatically add admin authentication headers
apiClient.interceptors.request.use(
  (config) => {
    const authHeaders = getAdminAuthHeaders();
    Object.assign(config.headers, authHeaders);

    console.log('API Request Headers:', {
      hasAuth: !!config.headers['Authorization'],
      hasAdminKey: !!config.headers['x-admin-key'],
      url: config.url
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Log requests/responses for easier debugging
apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const cfg = error?.config || {};
    const url = cfg?.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : (cfg.url || '');
    /* eslint-disable no-console */
    console.error('API Error:', {
      method: (cfg.method || 'get').toUpperCase(),
      url,
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message
    });
    /* eslint-enable no-console */
    return Promise.reject(error);
  }
);

export const uploadClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  withCredentials: true
});

uploadClient.interceptors.request.use(
  (config) => {
    const authHeaders = getAdminAuthHeaders();
    Object.assign(config.headers, authHeaders);

    // Let the browser set multipart/form-data boundary automatically
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

uploadClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const cfg = error?.config || {};
    const url = cfg?.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : (cfg.url || '');
    /* eslint-disable no-console */
    console.error('Upload API Error:', {
      method: (cfg.method || 'post').toUpperCase(),
      url,
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message
    });
    /* eslint-enable no-console */
    return Promise.reject(error);
  }
);

export const endpoints = {
  registerVendor: '/api/vendors/register',
  loginVendor: '/api/vendors/login',
  loginAdmin: '/api/admin/login',
  forgotPassword: '/api/vendor/forgot-password',
  resetPassword: '/api/vendor/reset-password',
  updateLocation: (id) => `/api/vendors/${id}/location`,
  uploadKyc: (id) => `/api/vendors/${id}/kyc`,
  kycStatus: (id) => `/api/vendors/${id}/kyc-status`,
  kycDocs: (id) => `/api/vendors/${id}/kyc-docs`,
  deleteKycDoc: (vendorId, docId) => `/api/vendors/${vendorId}/kyc-docs/${docId}`,
  
  // New comprehensive KYC endpoints
  kycDashboard: (id) => `/api/kyc/dashboard/${id}`,
  kycDocuments: (id) => `/api/kyc/documents/${id}`,
  kycUpload: (id) => `/api/kyc/upload/${id}`,
  kycDocumentDelete: (docId) => `/api/kyc/document/${docId}`,
  kycDocumentDownload: (docId) => `/api/kyc/document/${docId}/download`,
  kycNotifications: (id) => `/api/kyc/notifications/${id}`,
  kycNotificationRead: (notificationId) => `/api/kyc/notifications/${notificationId}/read`,
  kycTemplates: '/api/kyc/templates',
  kycOverallStatus: (id) => `/api/kyc/status/${id}`,
  adminDashboardSummary: '/api/admin/dashboard/summary',
  adminInventorySummary: '/api/admin/inventory/summary',
  adminVendors: '/api/admin/vendors',
  adminPayouts: '/api/admin/payouts',
  adminKycDocuments: '/api/admin/kyc-documents',
  adminKycDocumentStatus: (docId) => `/api/admin/kyc-documents/${docId}/status`,
  adminKycRunOCR: (docId) => `/api/admin/kyc/${docId}/ocr`,
  adminKycVerify: (docId) => `/api/admin/kyc/${docId}/verify`,
  adminKycAudit: '/api/admin/kyc-audit',
  adminKycDownload: (docId) => `/api/admin/documents/${docId}/download`,
  adminKycSummary: '/api/admin/kyc-summary',
  // Admin Reports
  adminReport_VendorsActiveInactive: '/api/admin/reports/vendors/active-inactive',
  adminReport_VendorPerformance: '/api/admin/reports/vendors/performance',
  adminReport_VendorApprovalHistory: '/api/admin/reports/vendors/approval-history',
  adminReport_ProductSalesRevenue: '/api/admin/reports/products/sales-revenue',
  adminReport_LowStock: '/api/admin/reports/products/low-stock',
  adminReport_TopLeastProducts: '/api/admin/reports/products/top-least',
  adminReport_ProductPriceHistory: '/api/admin/reports/products/price-history',
  adminReport_StockMovements: '/api/admin/reports/inventory/stock-movements',
  adminReport_AvailableVsReserved: '/api/admin/reports/inventory/available-vs-reserved',
  adminReport_HistoricalStock: '/api/admin/reports/inventory/historical-stock',
  adminReport_OrdersByStatus: '/api/admin/reports/orders/by-status',
  adminReport_PaymentStatus: '/api/admin/reports/orders/payment-status',
  adminReport_OrdersAggregate: '/api/admin/reports/orders/aggregate',
  adminReport_KycVerificationStatus: '/api/admin/reports/kyc/verification-status',
  adminReport_KycExpiringDocuments: '/api/admin/reports/kyc/expiring-documents',
  adminReport_KycAuditLog: '/api/admin/reports/kyc/audit-log',
  // Admin Reports: Export
  adminReport_Export: '/api/admin/reports/export/:type',
  // Drill-down endpoints (use existing resources where possible)
  adminOrders: '/api/admin/orders',
  adminOrderById: (id) => `/api/admin/orders/${id}`,
  packageOrders: '/api/admin/package-orders',
  packageOrderById: (id) => `/api/admin/package-orders/${id}`,
  adminProducts: '/api/admin/products',
  adminProductPriceHistory: (id) => `/api/admin/products/${id}/price-history`,
  // App ads endpoints (with ad-blocker safe alias)
  adminAds: '/api/admin/app-banners',
  adminAdsLegacy: '/api/admin/ads',
  adminAdById: (id) => `/api/admin/app-banners/${id}`,
  adminAdByIdLegacy: (id) => `/api/admin/ads/${id}`,
  adminAdStatus: (id) => `/api/admin/app-banners/${id}/status`,
  adminAdStatusLegacy: (id) => `/api/admin/ads/${id}/status`,
  adminVendorsList: '/api/admin/vendors',
  // Delivery partner management (admin)
  adminDeliveryPartners: '/api/admin/delivery/partners',
  adminDeliveryPartnerById: (id) => `/api/admin/delivery/partner/${id}`,
  adminDeliveryPartnerApprove: (id) => `/api/admin/delivery/partner/${id}/approve`,
  adminDeliveryPartnerReject: (id) => `/api/admin/delivery/partner/${id}/reject`,
  adminDeliveryPartnerBlock: (id) => `/api/admin/delivery/partner/${id}/block`,
  adminDeliveryPartnerResetPassword: (id) => `/api/admin/delivery/partner/${id}/reset-password`,
  adminDeliveryPartnerCreate: '/api/admin/delivery/partner',
  // Delivery live tracking
  adminDeliveryLiveTracking: '/api/admin/delivery/live-tracking',
  // Aliases per requested routes
  adminDocApprove: (docId) => `/api/admin/documents/${docId}/approve`,
  adminDocReject: (docId) => `/api/admin/documents/${docId}/reject`,
  adminDocOCR: (docId) => `/api/admin/documents/${docId}/ocr`,
  adminDocVerify: (docId) => `/api/admin/documents/${docId}/verify`,
  adminDocDelete: (docId) => `/api/admin/documents/${docId}/delete`,
  adminDocDownload: (docId) => `/api/admin/documents/${docId}/download`,
  // Payments
  paymentsList: '/api/payments',
  paymentById: (id) => `/api/payments/${id}`,
  paymentRefund: (id) => `/api/payments/${id}/refund`,
  paymentReprocess: (id) => `/api/payments/${id}/reprocess`,
  paymentsBulk: '/api/payments/bulk',
};

export default apiClient;
